/**
 * Troca o cartão por um token, direto com a Pagar.me.
 *
 * A requisição sai do NAVEGADOR do cliente para a API da Pagar.me — nunca passa
 * pelo nosso servidor. É o ponto inteiro deste arquivo: com o número do cartão
 * trafegando pelo nosso backend, a loja cairia no escopo pesado do PCI-DSS
 * (auditoria anual, segregação de rede, política de retenção de log) para
 * guardar um dado que ninguém quer guardar.
 *
 * A autenticação é a chave PÚBLICA (`pk_...`), que vai no HTML de qualquer loja
 * que aceite cartão. Ela não cobra nada: só serve para tokenizar. Quem cobra é
 * a chave secreta, que fica no servidor e nunca sai de lá.
 *
 * Não usamos o SDK da Pagar.me: são duas linhas de `fetch`, e um script externo
 * no checkout é mais uma coisa que pode não carregar bem na hora de pagar.
 */

const ENDPOINT = "https://api.pagar.me/core/v5/tokens";

/** "MM/AA" → { mes: "12", ano: "28" }. Aceita AAAA e devolve os dois dígitos. */
function separaValidade(exp) {
  const [mes = "", ano = ""] = String(exp || "").split("/");
  return { mes: mes.trim(), ano: ano.trim().slice(-2) };
}

/**
 * @param {string} publicKey  `pk_...` — vem de /settings, e só existe quando a loja está pronta para cobrar
 * @param {object} cartao     { number, holder_name, exp, cvv }
 * @returns {Promise<string>} o token de uso único
 */
export async function tokenizeCard(publicKey, cartao) {
  const { mes, ano } = separaValidade(cartao.exp);

  const res = await fetch(`${ENDPOINT}?appId=${encodeURIComponent(publicKey)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "card",
      card: {
        number: String(cartao.number || "").replace(/\s/g, ""),
        holder_name: String(cartao.holder_name || "").trim(),
        exp_month: Number(mes),
        exp_year: Number(ano),
        cvv: String(cartao.cvv || "").trim(),
      },
    }),
  });

  const body = await res.json().catch(() => ({}));

  if (!res.ok || !body?.id) {
    /* A Pagar.me devolve o erro por campo (`errors: { "card.number": [...] }`).
       Repassar a primeira mensagem é o que diz à pessoa QUAL campo está errado
       — um "não foi possível processar" genérico a faria conferir os quatro. */
    const detalhe = body?.errors
      ? Object.values(body.errors).flat()[0]
      : body?.message;
    throw new Error(detalhe || "Não foi possível validar o cartão. Confira os dados e tente de novo.");
  }

  return body.id;
}
